import React, { useCallback, useMemo } from 'react';
import { ChevronRight } from '@core/assets/ChevronRight';
import { useVisibilityObserver } from '@core/hooks/useVisibilityObserver';
import { useContextTranslation } from '@core/i18n';
import { getCTATranslation } from '@core/i18n/cta/getCTATranslation';
import { useContextParams } from '@core/params/context';
import { usePIPTarget } from '@core/pictureInPicture/usePIPTarget';
import { useProductImage } from '@core/shopping/hooks/useProductImage';
import { useSelectedOptionProductUnit } from '@core/shopping/hooks/useSelectedOptionProductUnit';
import { getProductUrl } from '@core/shopping/utils/product';
import { PRODUCT_CARD, PRODUCT_CARD_ITEM_IMG, PRODUCT_CARD_ITEM_NAME, PRODUCT_CARD_ITEM_PRICE, PRODUCT_CARD_ITEM_UNIT_NAME, } from '@player/testIds';
import { PriceLabel } from './PriceLabel';
import { ProductCardImageFallback } from './ProductCardImageFallback';
import { LabelWrapper, PriceWrapper, ProductCardCTA, ProductCardCTAText, ProductCardInfoWrapper, ProductCardInnerWrapper, ProductCardWrapper, ProductDetailWrapper, ProductImage, ProductUnitName, } from './ProductCardItem.styles';
/**
 * Reusable product card component
 */
export const ProductCardItem = ({ cardSize, compactSize, isSoldOut, onClick, onVisibilityChange, product, showCTA, }) => {
    const { t } = useContextTranslation();
    const params = useContextParams();
    const PRODUCT_CARD_VISIBILITY_THRESHOLD = 0.95;
    const selectedOptionProductUnit = useSelectedOptionProductUnit({ product });
    const { productImageSrc, currentImageSrc, altText } = useProductImage({
        product,
        unit: selectedOptionProductUnit,
    });
    const isRTL = params?.dir === 'rtl';
    const { url, target, onClick: onClickPIP, } = usePIPTarget({
        params,
        url: getProductUrl(product, selectedOptionProductUnit),
    });
    const onClickHandler = useCallback((event) => {
        onClick?.(event);
        onClickPIP(event);
    }, [onClick, onClickPIP]);
    const isProductPriceHidden = Boolean(product.product_hide_price) && Boolean(params?.hide_product_price);
    /**
     * Observe visibility and trigger callback
     */
    const onVisibilityObserverChange = useCallback((isVisible) => {
        onVisibilityChange?.(isVisible);
    }, [onVisibilityChange]);
    const cardItemRef = useVisibilityObserver({
        onChange: onVisibilityObserverChange,
        threshold: [PRODUCT_CARD_VISIBILITY_THRESHOLD],
    });
    function getButtonText() {
        switch (true) {
            case isProductPriceHidden:
                return t('View More');
            case isSoldOut ||
                product.product_units.every((unit) => unit.available === false):
                return t('Sold Out');
            default:
                return product.custom_cta_title
                    ? getCTATranslation(t, product.custom_cta_title)
                    : getCTATranslation(t, 'shop_now');
        }
    }
    const getProductImageSrc = useMemo(() => {
        if (selectedOptionProductUnit && currentImageSrc) {
            return currentImageSrc;
        }
        return productImageSrc;
    }, [currentImageSrc, productImageSrc, selectedOptionProductUnit]);
    //Force unit price if all product units have the same unit_original_price
    //https://fwn.atlassian.net/browse/CS-5640
    const forceUnitPrice = useMemo(() => product.product_units.every((unit) => unit.unit_original_price ===
        product.product_units[0].unit_original_price), [product.product_units]);
    const renderCTA = showCTA !== false;
    return (<ProductCardWrapper borderStyle={params?.ui_border_style} cardSize={cardSize} showCTA={renderCTA} ref={cardItemRef} onClick={onClickHandler} data-testid={PRODUCT_CARD} href={url} target={target}>
      <ProductCardInnerWrapper compactSize={compactSize}>
        <ProductImage borderStyle={params?.ui_border_style} compactSize={compactSize} src={getProductImageSrc} alt={altText} data-testid={PRODUCT_CARD_ITEM_IMG} fallbackComponent={<ProductCardImageFallback compactSize={compactSize}/>} loading="lazy"/>
        {!compactSize && (<ProductCardInfoWrapper>
            <ProductDetailWrapper>
              <LabelWrapper data-testid={PRODUCT_CARD_ITEM_NAME}>
                {product.product_name}
              </LabelWrapper>
              {selectedOptionProductUnit?.unit_name ? (<ProductUnitName data-testid={PRODUCT_CARD_ITEM_UNIT_NAME}>
                  {selectedOptionProductUnit?.unit_name}
                </ProductUnitName>) : null}
              <PriceWrapper data-testid={PRODUCT_CARD_ITEM_PRICE}>
                <PriceLabel product={product} gapBetweenPrices="0px 4px" originalPriceFontSize={10} unit={selectedOptionProductUnit} forceUnitPrice={!!selectedOptionProductUnit || forceUnitPrice}/>
              </PriceWrapper>
            </ProductDetailWrapper>
          </ProductCardInfoWrapper>)}
      </ProductCardInnerWrapper>

      {renderCTA ? (<ProductCardCTA>
          <ProductCardCTAText>{getButtonText()}</ProductCardCTAText>
          <ChevronRight isRTL={isRTL} width={'12px'} height={'12px'} label="" aria-hidden/>
        </ProductCardCTA>) : null}
    </ProductCardWrapper>);
};
